import { getPrimaryFunctionAppRoot } from '../../utils/functionAppRoot';
import { parseBlobIdFromImageUrl } from '../../utils/blobImageUrl';

const IMG_SRC_RE = /(<img\b[^>]*\bsrc=")([^"]+)(")/gi;

async function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error ?? new Error('Could not read image.'));
    reader.readAsDataURL(blob);
  });
}

async function fetchBlobImageDataUrl(blobId: string, idToken: string): Promise<string | null> {
  try {
    const endpoint = `${getPrimaryFunctionAppRoot()}/GetBlob?id=${encodeURIComponent(blobId)}&it=${idToken}`;
    const response = await fetch(endpoint);
    if (!response.ok) return null;
    const blob = await response.blob();
    if (!blob.type.startsWith('image/')) return null;
    return await blobToDataUrl(blob);
  } catch {
    return null;
  }
}

/** Replace authenticated GetBlob <img> srcs with data URLs so print windows can render them. */
export async function inlineAuthenticatedBlobImagesInHtml(
  html: string,
  idToken: string | null | undefined
): Promise<string> {
  if (!html || !idToken) return html;

  const replacements = new Map<string, string>();
  for (const match of html.matchAll(IMG_SRC_RE)) {
    const src = match[2]?.replace(/&amp;/g, '&') ?? '';
    const blobId = parseBlobIdFromImageUrl(src);
    if (!blobId || replacements.has(src)) continue;
    const dataUrl = await fetchBlobImageDataUrl(blobId, idToken);
    if (dataUrl) replacements.set(src, dataUrl);
  }

  if (replacements.size === 0) return html;

  return html.replace(IMG_SRC_RE, (full, prefix: string, src: string, suffix: string) => {
    const decoded = src.replace(/&amp;/g, '&');
    const dataUrl = replacements.get(decoded) ?? replacements.get(src);
    return dataUrl ? `${prefix}${dataUrl}${suffix}` : full;
  });
}
